// src/context/MaintenanceContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthProvider';

const MaintenanceContext = createContext();

export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceMode must be used within MaintenanceProvider');
  }
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const { userRole, authToken, isAuthenticated } = useAuth(); // Added authToken and isAuthenticated
  
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isLoading: false,
    isMaintenanceMode: false,
    maintenanceTools: [],
    error: null,
  });

  const checkMaintenanceStatus = useCallback(async (token) => {
    if (!token) {
      console.warn('No token provided for maintenance check');
      return null;
    }

    // Skip maintenance check for superadmin entirely
    if (userRole?.trim().toLowerCase() === 'superadmin') {
      console.log('Super admin detected - skipping maintenance check');
      setMaintenanceStatus({
        isLoading: false,
        isMaintenanceMode: false,
        maintenanceTools: [],
        error: null,
      });
      return {
        isMaintenanceMode: false,
        maintenanceTools: [],
      };
    }

    try {
      setMaintenanceStatus(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/maintenance`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch maintenance status');
      }

      const data = await response.json();
      
      const toolsInMaintenance = data
        .filter(tool => 
          tool.ai_integration && 
          tool.ai_integration.some(ai => ai.status === "maintenance")
        )
        .map(tool => ({
          toolId: tool.tool_id,
          toolName: tool.tool_name,
          category: tool.tool_category,
        }));

      const isInMaintenance = toolsInMaintenance.length > 0;
      
      setMaintenanceStatus({
        isLoading: false,
        isMaintenanceMode: isInMaintenance,
        maintenanceTools: toolsInMaintenance,
        error: null,
      });

      console.log('Maintenance check completed:', {
        isInMaintenance,
        toolsInMaintenance,
      });

      return {
        isMaintenanceMode: isInMaintenance,
        maintenanceTools: toolsInMaintenance,
      };
    } catch (error) {
      console.error('Error checking maintenance status:', error);
      setMaintenanceStatus({
        isLoading: false,
        isMaintenanceMode: false,
        maintenanceTools: [],
        error: error.message,
      });
      return null;
    }
  }, [userRole]);

  // AUTO-CHECK MAINTENANCE ON PAGE LOAD/REFRESH
  useEffect(() => {
    if (isAuthenticated && authToken && userRole) {
      //console.log('Auto-checking maintenance status on mount/refresh');
      checkMaintenanceStatus(authToken);
    }
  }, [isAuthenticated, authToken, userRole, checkMaintenanceStatus]);

  // Check if a specific page/tool is in maintenance
  const isPageInMaintenance = useCallback((pageName) => {
    // Wait until user role is loaded
    if (!userRole) {
      console.log('User role not yet loaded');
      return false;
    }

    const role = userRole.trim().toLowerCase();

    // ONLY superadmin bypass (exact match)
    if (role === 'superadmin') {
      return false;
    }

    // Admin and other users WILL see maintenance
    if (!maintenanceStatus.isMaintenanceMode) return false;
    
    const pageToCategory = {
      'dashboard': 'all',
      'projects': 'all',
      'issues': 'staticAnalysisTools',
      'testing': 'unitTestingTools',
      'reporting': 'all',
    };

    const category = pageToCategory[pageName];
    
    if (category === 'all') {
      return maintenanceStatus.maintenanceTools.length > 0;
    }
    
    return maintenanceStatus.maintenanceTools.some(
      tool => tool.category === category
    );
  }, [maintenanceStatus, userRole]);

  const value = {
    ...maintenanceStatus,
    checkMaintenanceStatus,
    isPageInMaintenance,
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};